import { Injectable, WritableSignal, signal } from '@angular/core';

interface InlineState {
  count: number;
  messageKey: string | null;
}

/**
 * Service-driven inline loading state — same pattern as LoadingService.
 * Callers only call show/hide; the InlineLoadingComponent placed in the
 * template reads state from this service by key.
 */
@Injectable({ providedIn: 'root' })
export class InlineLoadingService {
  private readonly states = new Map<string, WritableSignal<InlineState>>();

  private state(key: string): WritableSignal<InlineState> {
    if (!this.states.has(key)) {
      this.states.set(key, signal({ count: 0, messageKey: null }));
    }
    return this.states.get(key)!;
  }

  /**
   * Start loading for the given key.
   * @param key      Unique identifier (matches [loadingKey] on app-inline-loading)
   * @param messageKey  Optional i18n key for the loading message
   */
  show(key = 'default', messageKey?: string): void {
    this.state(key).update((s) => ({
      count: s.count + 1,
      messageKey: messageKey ?? s.messageKey,
    }));
  }

  /** Stop loading for the given key. */
  hide(key = 'default'): void {
    this.state(key).update((s) => {
      const count = Math.max(0, s.count - 1);
      return { count, messageKey: count > 0 ? s.messageKey : null };
    });
  }

  /** Returns the state signal for the given key (read-only). */
  getState(key = 'default') {
    return this.state(key).asReadonly();
  }
}
